import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaPhoneAlt, FaShoppingBag, FaTag, FaWhatsapp } from "react-icons/fa";
import apiClient from "../lib/apiClient";
import { APP_CONFIG } from "../config/appConfig";
import {
  PRODUCT_CATEGORY_OPTIONS,
  getProductCategoryLabel,
  getProductSubcategoryLabel,
  getProductSubcategoryOptions,
} from "../config/productTaxonomy";

const WHATSAPP_NUMBER = "15147788318";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "Prix sur demande";
  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: APP_CONFIG.currency || "CAD",
  }).format(Number(value));
};

const buildWhatsappLink = (productName) => {
  const message = encodeURIComponent(
    `Bonjour Mr. Renaudin Barbershop, j'aimerais avoir un conseil sur le produit: ${productName}.`
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
};

const useProductsStyles = () => {
  useEffect(() => {
    const styleId = "mr-renaudin-products-page-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .pr-root {
        --pr-bg: #0b0d10;
        --pr-ink: #f7f1e8;
        --pr-muted: #aeb7c0;
        --pr-panel: #15191f;
        --pr-panel-2: #1e252d;
        --pr-line: rgba(247,241,232,0.13);
        --pr-gold: #d6aa4b;
        --pr-green: #71816e;
        --pr-clay: #9b5b45;
        background: var(--pr-bg);
        color: var(--pr-ink);
        font-family: 'DM Sans', Arial, sans-serif;
        min-height: 100vh;
      }
      .pr-root * { box-sizing: border-box; }
      .pr-shell { width: min(1180px, calc(100% - 32px)); margin: 0 auto; }
      .pr-eyebrow {
        color: var(--pr-gold);
        font-size: 0.72rem;
        font-weight: 900;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        margin: 0 0 1rem;
      }
      .pr-title {
        font-family: 'Playfair Display', Georgia, serif;
        font-size: clamp(2.55rem, 7vw, 6.4rem);
        line-height: 0.96;
        letter-spacing: 0;
        margin: 0;
      }
      .pr-copy {
        color: var(--pr-muted);
        line-height: 1.8;
        margin: 0;
      }
      .pr-btn {
        min-height: 46px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.55rem;
        border: 1px solid var(--pr-line);
        padding: 0.85rem 1.1rem;
        font-size: 0.8rem;
        font-weight: 900;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        text-decoration: none;
        cursor: pointer;
        transition: transform 0.2s, border-color 0.2s, background 0.2s, color 0.2s;
      }
      .pr-btn:hover, .pr-btn:focus-visible {
        transform: translateY(-2px);
        border-color: var(--pr-gold);
        outline: none;
      }
      .pr-btn.primary { background: var(--pr-gold); border-color: var(--pr-gold); color: #111; }
      .pr-btn.dark { background: rgba(21,25,31,0.88); color: var(--pr-ink); }

      .pr-hero {
        position: relative;
        min-height: 76svh;
        display: flex;
        align-items: flex-end;
        overflow: hidden;
        isolation: isolate;
        padding: 6rem 0 3rem;
        border-bottom: 1px solid var(--pr-line);
      }
      .pr-hero-bg {
        position: absolute;
        inset: 0;
        z-index: -2;
        background: linear-gradient(135deg, rgba(113,129,110,0.36), rgba(11,13,16,0.9) 58%, rgba(155,91,69,0.22));
      }
      .pr-hero-bg.has-image {
        background-size: cover;
        background-position: center;
      }
      .pr-hero::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        background:
          linear-gradient(90deg, rgba(11,13,16,0.95), rgba(11,13,16,0.64) 48%, rgba(11,13,16,0.25)),
          linear-gradient(0deg, rgba(11,13,16,1), transparent 42%);
      }
      .pr-hero-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.75fr);
        gap: 2rem;
        align-items: end;
      }
      .pr-hero-copy { max-width: 720px; display: grid; gap: 1.35rem; }
      .pr-hero-copy .pr-copy { max-width: 640px; font-size: 1.05rem; }
      .pr-proof {
        border: 1px solid var(--pr-line);
        background: rgba(21,25,31,0.76);
        padding: 1.2rem;
        display: grid;
        gap: 0.85rem;
      }
      .pr-proof-row {
        display: flex;
        gap: 0.75rem;
        align-items: flex-start;
        color: var(--pr-muted);
        font-size: 0.9rem;
        line-height: 1.55;
      }
      .pr-proof-row svg { color: var(--pr-gold); margin-top: 0.2rem; flex-shrink: 0; }

      .pr-section { padding: 5rem 0; }
      .pr-section-head {
        display: grid;
        grid-template-columns: minmax(0, 0.72fr) minmax(260px, 0.28fr);
        gap: 2rem;
        align-items: end;
        margin-bottom: 2rem;
      }
      .pr-section-head h2 {
        font-family: 'Playfair Display', Georgia, serif;
        font-size: clamp(2rem, 4vw, 3.7rem);
        line-height: 1;
        margin: 0.5rem 0 0;
      }
      .pr-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 0.55rem;
      }
      .pr-filter-zone {
        display: grid;
        gap: 0.75rem;
        margin-bottom: 1.35rem;
      }
      .pr-toolbar.subcategory {
        border-left: 2px solid var(--pr-gold);
        padding-left: 0.75rem;
      }
      .pr-filter {
        border: 1px solid var(--pr-line);
        background: var(--pr-panel);
        color: var(--pr-ink);
        padding: 0.65rem 0.85rem;
        cursor: pointer;
        font-size: 0.76rem;
        font-weight: 900;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .pr-filter.active {
        background: var(--pr-gold);
        color: #111;
        border-color: var(--pr-gold);
      }
      .pr-filter.subtle {
        background: rgba(247,241,232,0.05);
      }
      .pr-results-note {
        color: var(--pr-muted);
        font-size: 0.82rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin-bottom: 1rem;
      }
      .pr-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
        gap: 1rem;
      }
      .pr-card {
        border: 1px solid var(--pr-line);
        background: var(--pr-panel);
        min-height: 100%;
        display: grid;
        grid-template-rows: auto 1fr;
      }
      .pr-media {
        aspect-ratio: 4 / 3;
        background: var(--pr-panel-2);
        border-bottom: 1px solid var(--pr-line);
        overflow: hidden;
      }
      .pr-media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.4s ease;
      }
      .pr-card:hover .pr-media img { transform: scale(1.035); }
      .pr-no-photo {
        width: 100%;
        height: 100%;
        display: grid;
        place-items: center;
        padding: 1rem;
        text-align: center;
        color: var(--pr-muted);
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-size: 0.76rem;
      }
      .pr-card-body {
        padding: 1.15rem;
        display: grid;
        gap: 0.8rem;
      }
      .pr-card-top {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
        align-items: flex-start;
      }
      .pr-card h3 {
        margin: 0;
        color: var(--pr-ink);
        font-size: 1.05rem;
        line-height: 1.25;
      }
      .pr-price {
        color: var(--pr-gold);
        font-weight: 900;
        white-space: nowrap;
      }
      .pr-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .pr-chip {
        border: 1px solid var(--pr-line);
        color: var(--pr-muted);
        padding: 0.35rem 0.55rem;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
      .pr-card p { margin: 0; color: var(--pr-muted); line-height: 1.65; font-size: 0.92rem; }
      .pr-card-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.55rem;
        margin-top: auto;
      }
      .pr-card-actions .pr-btn { flex: 1 1 160px; padding-inline: 0.85rem; }
      .pr-empty {
        border: 1px solid var(--pr-line);
        background: var(--pr-panel);
        padding: 3rem;
        text-align: center;
        display: grid;
        gap: 1rem;
        justify-items: center;
      }
      .pr-empty h2 {
        font-family: 'Playfair Display', Georgia, serif;
        font-size: clamp(1.9rem, 4vw, 3.2rem);
        line-height: 1;
        margin: 0;
      }
      .pr-empty p { max-width: 620px; }

      .pr-care-band {
        border-top: 1px solid var(--pr-line);
        background: #111418;
        padding: 4rem 0;
      }
      .pr-care-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
        gap: 1rem;
      }
      .pr-care-item {
        border: 1px solid var(--pr-line);
        padding: 1.2rem;
        background: rgba(247,241,232,0.03);
      }
      .pr-care-item svg { color: var(--pr-gold); margin-bottom: 0.8rem; }
      .pr-care-item h3 { margin: 0 0 0.55rem; font-size: 1rem; }
      .pr-care-item p { margin: 0; color: var(--pr-muted); line-height: 1.65; font-size: 0.9rem; }

      @media (max-width: 920px) {
        .pr-hero-grid, .pr-section-head { grid-template-columns: 1fr; }
        .pr-care-grid { grid-template-columns: 1fr; }
      }
      @media (max-width: 620px) {
        .pr-hero { min-height: auto; padding: 5rem 0 2.6rem; }
        .pr-grid { grid-template-columns: 1fr; }
        .pr-section { padding: 4rem 0; }
        .pr-toolbar {
          flex-wrap: nowrap;
          overflow-x: auto;
          padding-bottom: 0.3rem;
          scrollbar-width: thin;
        }
        .pr-filter { flex: 0 0 auto; }
        .pr-card-top { display: grid; }
        .pr-price { white-space: normal; }
        .pr-empty { padding: 2rem 1.2rem; }
      }
    `;
    document.head.appendChild(style);
    return () => document.getElementById(styleId)?.remove();
  }, []);
};

const ProductsPage = () => {
  useProductsStyles();
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("tout");
  const [activeSubcategory, setActiveSubcategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/api/products")
      .then(res => setProducts(res.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const featuredProduct = products.find(product => product.is_featured && product.image_data)
    || products.find(product => product.image_data);

  const categories = useMemo(() => {
    const configuredCategories = PRODUCT_CATEGORY_OPTIONS.map(category => category.value);
    const dynamicCategories = Array.from(new Set(products.map(product => product.category).filter(Boolean)))
      .filter(category => !configuredCategories.includes(category))
      .map(category => ({ value: category, label: getProductCategoryLabel(category) }));

    return [
      { value: "tout", label: "Tout" },
      ...PRODUCT_CATEGORY_OPTIONS,
      ...dynamicCategories,
    ];
  }, [products]);

  const activeSubcategoryOptions = getProductSubcategoryOptions(activeCategory);
  const activeCategoryLabel = getProductCategoryLabel(activeCategory);

  const displayedProducts = products.filter(product => {
    const matchesCategory = activeCategory === "tout" || product.category === activeCategory;
    const matchesSubcategory = !activeSubcategory || product.subcategory === activeSubcategory;
    return matchesCategory && matchesSubcategory;
  });

  const handleCategoryFilter = (category) => {
    setActiveCategory(category);
    setActiveSubcategory("");
  };

  return (
    <main className="pr-root">
      <Helmet>
        <title>Produits | Mr. Renaudin Barbershop</title>
        <meta name="description" content="Decouvrez les produits recommandes par Mr. Renaudin Barbershop a Shawinigan pour entretenir votre coupe, votre barbe et votre style entre deux rendez-vous." />
      </Helmet>

      <section className="pr-hero">
        <div
          className={`pr-hero-bg ${featuredProduct ? "has-image" : ""}`}
          style={featuredProduct ? { backgroundImage: `url(${featuredProduct.image_data})` } : undefined}
          aria-hidden="true"
        />
        <div className="pr-shell pr-hero-grid">
          <div className="pr-hero-copy">
            <p className="pr-eyebrow">Selection professionnelle au salon</p>
            <h1 className="pr-title">Gardez une coupe nette plus longtemps.</h1>
            <p className="pr-copy">
              Soins, accessoires et produits de finition choisis au salon pour garder une
              coupe propre, une barbe disciplinee et une routine simple entre deux rendez-vous.
            </p>
            <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
              <Link to="/reserver" className="pr-btn primary"><FaShoppingBag /> Reserver une coupe</Link>
              <a href={`tel:${APP_CONFIG.phone}`} className="pr-btn dark"><FaPhoneAlt /> Appeler le salon</a>
            </div>
          </div>

          <aside className="pr-proof" aria-label="Avantages des produits du salon">
            <div className="pr-proof-row"><FaCheckCircle /><span>Conseils directs au fauteuil selon votre coupe, votre texture et votre routine.</span></div>
            <div className="pr-proof-row"><FaCheckCircle /><span>Selection dynamique mise a jour selon les arrivages du salon.</span></div>
            <div className="pr-proof-row"><FaCheckCircle /><span>Demandez le bon produit avant ou apres votre rendez-vous.</span></div>
          </aside>
        </div>
      </section>

      <section className="pr-section">
        <div className="pr-shell">
          <div className="pr-section-head">
            <div>
              <p className="pr-eyebrow">Catalogue Mr. Renaudin</p>
              <h2>Des produits choisis pour rester propre entre deux rendez-vous.</h2>
            </div>
            <p className="pr-copy">
              Filtrez la selection, reperez les produits disponibles et demandez conseil au salon.
            </p>
          </div>

          {loading ? (
            <div className="pr-empty">
              <h2>Chargement du catalogue...</h2>
            </div>
          ) : products.length === 0 ? (
            <div className="pr-empty">
              <h2>La selection arrive bientot.</h2>
              <p className="pr-copy">
                Le salon prepare une vitrine de produits professionnels pour l'entretien
                des coupes, contours, textures et barbes. Pour une recommandation immediate,
                contactez directement Mr. Renaudin Barbershop.
              </p>
              <a href={buildWhatsappLink("conseil produit")} target="_blank" rel="noopener noreferrer" className="pr-btn primary">
                <FaWhatsapp /> Demander conseil
              </a>
            </div>
          ) : (
            <>
              <div className="pr-filter-zone">
                <div className="pr-toolbar" aria-label="Filtrer les produits par categorie">
                  {categories.map(category => (
                    <button
                      key={category.value}
                      className={`pr-filter ${activeCategory === category.value ? "active" : ""}`}
                      onClick={() => handleCategoryFilter(category.value)}
                      type="button"
                    >
                      {category.label}
                    </button>
                  ))}
                </div>

                {activeSubcategoryOptions.length > 0 && (
                  <div className="pr-toolbar subcategory" aria-label="Filtrer les soins de la peau par sous-categorie">
                    <button
                      className={`pr-filter subtle ${activeSubcategory === "" ? "active" : ""}`}
                      onClick={() => setActiveSubcategory("")}
                      type="button"
                    >
                      Tous les soins de la peau
                    </button>
                    {activeSubcategoryOptions.map(subcategory => (
                      <button
                        key={subcategory.value}
                        className={`pr-filter subtle ${activeSubcategory === subcategory.value ? "active" : ""}`}
                        onClick={() => setActiveSubcategory(subcategory.value)}
                        type="button"
                      >
                        {subcategory.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pr-results-note">
                {displayedProducts.length} produit{displayedProducts.length > 1 ? "s" : ""} dans {activeCategory === "tout" ? "tout le catalogue" : activeCategoryLabel}
              </div>

              {displayedProducts.length === 0 ? (
                <div className="pr-empty">
                  <h2>Aucun produit dans cette selection.</h2>
                  <p className="pr-copy">
                    Cette categorie n'a pas encore de produit publie. Consultez tout le catalogue
                    ou demandez directement au salon le soin le mieux adapte.
                  </p>
                  <button className="pr-btn primary" type="button" onClick={() => handleCategoryFilter("tout")}>
                    Voir tout le catalogue
                  </button>
                </div>
              ) : (
                <div className="pr-grid">
                  {displayedProducts.map(product => (
                  <article className="pr-card" key={product.id}>
                    <div className="pr-media">
                      {product.image_data ? (
                        <img src={product.image_data} alt={product.name} loading="lazy" />
                      ) : (
                        <div className="pr-no-photo">Photo a venir</div>
                      )}
                    </div>
                    <div className="pr-card-body">
                      <div className="pr-card-top">
                        <h3>{product.name}</h3>
                        <span className="pr-price">{formatPrice(product.price)}</span>
                      </div>
                      <div className="pr-meta">
                        {product.brand && <span className="pr-chip">{product.brand}</span>}
                        {product.category && <span className="pr-chip"><FaTag /> {getProductCategoryLabel(product.category)}</span>}
                        {product.subcategory && <span className="pr-chip">{getProductSubcategoryLabel(product.subcategory)}</span>}
                        <span className="pr-chip">{Number(product.stock_quantity || 0) > 0 ? "Disponible au salon" : "Sur demande"}</span>
                      </div>
                      {(product.short_description || product.description) && (
                        <p>{product.short_description || product.description}</p>
                      )}
                      <div className="pr-card-actions">
                        <a href={buildWhatsappLink(product.name)} target="_blank" rel="noopener noreferrer" className="pr-btn primary">
                          <FaWhatsapp /> Demander conseil
                        </a>
                        <Link to="/reserver" className="pr-btn dark">
                          Voir avec ma coupe
                        </Link>
                      </div>
                    </div>
                  </article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="pr-care-band">
        <div className="pr-shell pr-care-grid">
          <div className="pr-care-item">
            <FaCheckCircle />
            <h3>Conseil personnalise</h3>
            <p>Le produit doit suivre votre style, pas l'inverse. Le salon recommande ce qui sert vraiment votre coupe.</p>
          </div>
          <div className="pr-care-item">
            <FaCheckCircle />
            <h3>Routine simple</h3>
            <p>Des soins faciles a integrer le matin, pour garder des contours propres et une texture maitrisee.</p>
          </div>
          <div className="pr-care-item">
            <FaCheckCircle />
            <h3>Experience salon</h3>
            <p>Demandez une demonstration pendant le rendez-vous et repartez avec une methode claire.</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductsPage;
